// The gallery view (the home route "/") — extracted from the B1 shell so the shell
// can route between it, the claim screen, and the device manager (B2, #63). The
// behavior is unchanged from B1: load the Owner's media via `/api/media`, render a
// newest-first grid that lazy-loads each item straight from R2, and offer per-item
// download + cursor pagination.
//
// State machine: loading → (unauthorized | error | empty | grid). Test IDs +
// markup are identical to B1's so the gallery tests are unaffected.

import { useState } from "react";
import type { ApiClient, MediaItem, MediaTypeFilter } from "../api";
import { useMedia } from "../useMedia";
import type { SheetCores } from "../cores";
import type { BuildMosaicOptions, MosaicSelectionItem } from "../cores";
import { MediaGrid } from "./MediaGrid";
import { EmptyState } from "./EmptyState";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";
import { RefreshErrorBanner } from "./RefreshErrorBanner";
import { SignedOut } from "./SignedOut";

export interface GalleryProps {
  client: ApiClient;
  /** Injectable download flow (tests stub it); falls through to the real one otherwise. */
  onDownload?: (item: MediaItem) => Promise<void>;
  /** Injectable contact-Sheet decode/render seam, threaded to the cards (B3, #64). */
  sheetCores?: SheetCores;
  /** Injectable Mosaic builder, threaded to the picker overlay (B4, #65). */
  buildMosaic?: (
    items: MosaicSelectionItem[],
    options: BuildMosaicOptions,
  ) => Promise<Blob>;
  /** Take a Capture into the Look editor (palette/preview seed) — routes to /looks. */
  onUseInEditor?: (item: MediaItem) => void;
  /** Route to the claim flow — wired into the first-run empty state as onboarding (#135). */
  onClaim?: () => void;
}

export function Gallery({
  client,
  onDownload,
  sheetCores,
  buildMosaic,
  onUseInEditor,
  onClaim,
}: GalleryProps): React.JSX.Element {
  const [filter, setFilter] = useState<MediaTypeFilter>("all");
  // Archive visibility (ADR-0058): false → the default gallery, true → the Archived view.
  const [archived, setArchived] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);
  const media = useMedia(client, filter, archived);

  // Archive/Restore the given keys, then drop the mutated cards from the list IN PLACE
  // (media.dropItems also fixes up the counts + the "Archived (N)" badge). Deliberately not
  // a page-1 reload — that collapsed the accumulated pages and threw away the scroll
  // position mid-triage. A failure is CAUGHT and surfaced in an inline banner (it does not
  // ride the media-LOAD error path), and this never rejects — so the fire-and-forget
  // per-card handlers can't raise an unhandled promise rejection.
  async function runMutation(fn: () => Promise<unknown>, keys: string[]): Promise<void> {
    setMutating(true);
    setMutateError(null);
    try {
      await fn();
      media.dropItems(keys);
    } catch (err) {
      setMutateError(err instanceof Error ? err.message : "Couldn’t update — please try again.");
    } finally {
      setMutating(false);
    }
  }
  const onArchive = (keys: string[]): Promise<void> =>
    runMutation(() => client.archiveMedia(keys), keys);
  const onRestore = (keys: string[]): Promise<void> =>
    runMutation(() => client.restoreMedia(keys), keys);

  if (media.unauthorized) return <SignedOut />;
  if (media.loading) return <Loading label="Loading your gallery…" />;
  // Initial-load failure (nothing to show yet) → full-screen error. A failed REFETCH (filter
  // change / reload) instead keeps the previous grid + tabs and shows an inline banner (below),
  // so a transient blip can't strand the owner with no tabs to switch / retry from.
  if (media.error && media.items.length === 0 && !media.refetching) {
    return (
      <ErrorState
        title="Couldn’t load your media"
        message={media.error}
        onRetry={media.reload}
      />
    );
  }
  // Whole library empty — decided from the server's whole-library count (which persists across a
  // zero-result filter detour), not the transient loaded items, and suppressed during a refetch.
  // So switching back to "All" never flashes the first-run onboarding for an owner who has media.
  // Truly-empty = no VISIBLE media AND nothing Archived (ADR-0058) — otherwise "everything's
  // archived" would wrongly show the first-run onboarding. The Archived view's own emptiness is
  // handled inside the grid (with the toggle to switch back).
  const nothingArchived = media.archivedCount == null || media.archivedCount === 0;
  const libraryEmpty =
    (media.counts ? media.counts.all === 0 : media.items.length === 0) && nothingArchived;
  if (filter === "all" && !archived && libraryEmpty && !media.refetching) {
    return (
      <EmptyState
        title="No media yet"
        message="When a claimed camera Syncs over your home Wi-Fi, your Captures and Clips appear here — newest first."
        ctaLabel={onClaim ? "Claim your first camera" : undefined}
        onCta={onClaim}
      />
    );
  }

  return (
    <>
      {media.error && media.items.length > 0 && (
        <RefreshErrorBanner
          className="gallery__error"
          message={media.error}
          onRetry={media.reload}
        />
      )}
      {mutateError && (
        <p className="gallery__error" role="alert" data-testid="archive-error">
          {mutateError}
        </p>
      )}
      <MediaGrid
        client={client}
        items={media.items}
        filter={filter}
        onFilter={setFilter}
        counts={media.counts}
        total={media.total}
        refetching={media.refetching}
        archivedView={archived}
        onToggleArchived={() => setArchived((a) => !a)}
        archivedCount={media.archivedCount}
        onArchive={onArchive}
        onRestore={onRestore}
        mutating={mutating}
        hasMore={media.cursor != null}
        loadingMore={media.loadingMore}
        onLoadMore={media.loadMore}
        onDownload={onDownload}
        sheetCores={sheetCores}
        buildMosaic={buildMosaic}
        onUseInEditor={onUseInEditor}
        onReload={media.reload}
      />
    </>
  );
}
